import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgLoginReportRow, OrgLoginReportView, OrgReportsApi } from '@/services/org-reports.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Input';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';

const ROLE_OPTIONS = ['owner', 'admin', 'manager', 'school_admin', 'teacher', 'head_faculty', 'staff', 'caretaker', 'cashier', 'student', 'parent', 'guardian'];

/**
 * Login Report — org tier, read-only. Cross-school 'never logged in'
 * roster / 'last login' per user, matching the Finance/Video View report
 * pattern (a School filter, no further pagination — real cross-school row
 * counts are small in this project's migrated data). Ported from the
 * school-tier LoginReportPage.tsx — same two views, same real
 * `login_logs` caveat (new instrumentation, not migrated data, so a
 * freshly provisioned school shows no history until real logins accrue).
 */
export function OrgLoginReportPage() {
  const [schoolId, setSchoolId] = useState('');
  const [view, setView] = useState<OrgLoginReportView>('last');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-login-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const params = {
    schoolId: schoolId || undefined,
    view,
    role: role || undefined,
  };

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-login-report', params],
    queryFn: () => OrgReportsApi.loginReport(params),
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportLoginReport(format, params);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Login Report"
        description="Logins recorded going forward, across your organization's schools — a freshly provisioned school shows no history until real logins accrue."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
          {(['last', 'never'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium',
                view === v ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {v === 'last' ? 'Last Login' : 'Never Logged In'}
            </button>
          ))}
        </div>
        <Select className="!w-48" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Role</th>
                {view === 'last' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Last Login</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Type</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isFetching && !rows.length ? (
                <tr>
                  <td colSpan={view === 'last' ? 6 : 4} className="px-4 py-12 text-center text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={view === 'last' ? 6 : 4} className="px-4 py-12 text-center text-slate-400">
                    {view === 'last' ? 'No users found.' : 'Every user has logged in at least once.'}
                  </td>
                </tr>
              ) : (
                rows.map((r: OrgLoginReportRow) => (
                  <tr key={`${r.schoolId}-${r.userId}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{r.schoolName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.name}</td>
                    <td className="px-4 py-3 text-slate-700">{r.email ?? '—'}</td>
                    <td className="px-4 py-3">
                      <Badge tone="slate">{r.role}</Badge>
                    </td>
                    {view === 'last' && (
                      <>
                        <td className="px-4 py-3 text-slate-700">
                          {r.lastLoginAt ? formatDateTime(r.lastLoginAt) : <span className="text-xs uppercase tracking-wide text-amber-600">Never</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{r.lastLoginType ?? '—'}</td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
